/**
 * auth.ts — Store Zustand da sessão.
 *
 * Substitui o que na versão Next.js era feito em dois lugares:
 *   - `src/middleware.ts`      → checava o cookie `laravel_session` no Edge
 *   - `(dashboard)/layout.tsx` → chamava /api/auth/me em Server Component
 *
 * Como o build agora é 100% client-side, a validação passa a acontecer uma vez
 * no bootstrap (`fetchSession`) e o resultado fica aqui. O guard de rota
 * (`src/routes/dashboard-layout.tsx`) apenas lê este estado.
 *
 * O cookie de sessão continua sendo httpOnly e gerenciado pelo Laravel — nada
 * de token é persistido no browser por este store.
 */

import { create } from 'zustand'
import { apiFetch } from '@/lib/api'

export type UserRole = 'ADMIN' | 'HR' | 'MANAGER' | 'EMPLOYEE'

export interface SessionUser {
  id: string
  name: string
  email: string
  role: UserRole | string
}

/**
 * `idle`    — ainda não checamos a sessão
 * `loading` — request /api/auth/me em voo
 * `authed`  — sessão válida, `user` preenchido
 * `guest`   — sem sessão (401) ou backend indisponível
 */
export type AuthStatus = 'idle' | 'loading' | 'authed' | 'guest'

interface AuthState {
  status: AuthStatus
  user: SessionUser | null
  systemName: string
  /** Busca /api/auth/me e o nome do sistema. Idempotente por request em voo. */
  fetchSession: () => Promise<void>
  /** Chamado após login bem-sucedido para popular o store sem novo round-trip. */
  setUser: (user: SessionUser) => void
  clear: () => void
}

/** Dedupe: se duas rotas montarem ao mesmo tempo, só uma request sai. */
let inFlight: Promise<void> | null = null

export const useAuthStore = create<AuthState>((set) => ({
  status: 'idle',
  user: null,
  systemName: 'Módulo RH',

  fetchSession: () => {
    if (inFlight) return inFlight

    set({ status: 'loading' })

    inFlight = (async () => {
      try {
        const meRes = await apiFetch('/api/auth/me')

        if (!meRes.ok) {
          set({ status: 'guest', user: null })
          return
        }

        const me = await meRes.json()
        // me() retorna { user: { id, name, email, role } }
        const userData = me.user ?? me

        set({
          status: 'authed',
          user: {
            id: userData.id ?? '',
            name: userData.name ?? '',
            email: userData.email ?? '',
            role: userData.role ?? 'HR',
          },
        })

        // Nome do sistema — falha aqui não invalida a sessão.
        try {
          const cfgRes = await apiFetch('/api/system-config/system_name')
          if (cfgRes.ok) {
            const cfg = await cfgRes.json()
            if (cfg.value) set({ systemName: cfg.value })
          }
        } catch {
          // ignora erro de config — usa fallback
        }
      } catch {
        // Rede fora / backend indisponível — trata como visitante.
        set({ status: 'guest', user: null })
      } finally {
        inFlight = null
      }
    })()

    return inFlight
  },

  setUser: (user) => set({ status: 'authed', user }),

  clear: () => set({ status: 'guest', user: null }),
}))

/** Bypass de desenvolvimento, equivalente ao AUTH_DISABLED do middleware.ts. */
export const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === 'true'
